import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import CouriersService from "../../services/couriers.service";
import { BaseResponse } from "../../types/responses.type";
import { CourierResponse } from "types/trip.types";

interface CouriersState {
  couriers: CourierResponse[];
  courier: CourierResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: CouriersState = {
  couriers: [],
  courier: null,
  loading: false,
  error: null,
};

export const fetchCouriers = createAsyncThunk(
  "couriers/fetchCouriers",
  async (_, { rejectWithValue }) => {
    const res: BaseResponse<CourierResponse[]> =
      await CouriersService.getCouriers();
    if (res.ok) {
      return res.data;
    } else {
      return rejectWithValue(res.errorMessage || "Failed to fetch couriers");
    }
  },
);

export const fetchCourier = createAsyncThunk(
  "couriers/fetchCourier",
  async (id: number, { rejectWithValue }) => {
    const res: BaseResponse<CourierResponse> =
      await CouriersService.getCourier(id);
    if (res.ok) {
      return res.data;
    } else {
      return rejectWithValue(res.errorMessage || "Failed to fetch courier");
    }
  },
);

const couriersSlice = createSlice({
  name: "couriers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchCouriers
      .addCase(fetchCouriers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCouriers.fulfilled, (state, action) => {
        state.loading = false;
        state.couriers = action.payload || [];
      })
      .addCase(fetchCouriers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchCourier
      .addCase(fetchCourier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourier.fulfilled, (state, action) => {
        state.loading = false;
        state.courier = action.payload || null;
        if (action.payload) {
          const courierId = action.meta.arg;
          const courierIndex = state.couriers.findIndex(
            (c) => c.courierId === courierId,
          );
          if (courierIndex < 0) {
            state.couriers = [action.payload, ...(state.couriers ?? [])];
          } else {
            state.couriers.splice(courierIndex, 1, action.payload);
          }
        }
      })
      .addCase(fetchCourier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default couriersSlice.reducer;
